import React from "react";
import styled from "styled-components";
import { assetUrl } from '@bluecrew/web-react-core/src/constants';
import { CompanyWorker } from "../../../../../api/bluecrew/payroll";
import { StyledUserAvatar } from "../../../PayrollReports/PayrollReportsTable/styledComponents";
import { AddShiftPartialResult } from "../AddShiftForm.types";

// Main container
const PartialSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
  width: clamp(300px, 100%, 600px);
  margin-inline: auto;
`;

// Success section styling
const SuccessSection = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SuccessIconContainer = styled.div`
  flex-shrink: 0;
  border-radius: 50%;
	width: 36px;
	height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessText = styled.p`
  font-weight: 600;
  color: #111827;
  margin: 0;
  flex-grow: 1;
`;

// Failed section styling
const FailedSection = styled.div`
  background-color: #fef2f2;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  padding: 16px;
`;

const FailedHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const FailedIconContainer = styled.div`
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.5);
  padding: 8px;
  border-radius: 50%;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FailedContent = styled.div`
  flex-grow: 1;
`;

const FailedTitle = styled.h3`
  font-weight: 600;
  color: #dc2626;
  margin: 0 0 12px 0;
`;

const FailedWorkersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FailedWorkerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f9fafb;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const WorkerInfo = styled.div`
  flex-grow: 1;
`;

const WorkerName = styled.p`
  font-weight: 500;
  color: #111827;
  margin: 0 0 4px 0;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: #dc2626;
  margin: 0;
`;

const getFullName = (worker: CompanyWorker) => `${worker.firstName} ${worker.lastName}`;

export type FailedWorkersDisplayProps = {
  workerProps: Map<string, CompanyWorker>;
  workerResults: Array<AddShiftPartialResult>;
};

export const FailedWorkersDisplay = ({
  workerProps,
  workerResults,
}: FailedWorkersDisplayProps) => {
  const successfulWorkers = workerResults
    .filter(result => result.status === 'success')
    .map(result => result.userId);

  const failedUsers = workerResults
    .filter(result => result.status === 'error')
    .map(failed => {
      const worker = workerProps.get(failed.userId);
      return {
        userId: failed.userId,
        userName: worker ? getFullName(worker) : failed.userId,
        profilePicSrc: worker?.profilePicSrc?.toString(),
        errorMessage: failed.errorMessage || "Unknown error occurred",
      };
    });

  return (
    <PartialSuccessContainer>
      {successfulWorkers.length > 0 && (
        <SuccessSection>
          <SuccessIconContainer>
            <img
              src={`${assetUrl}/icons/success-modal-icon.svg`}
              alt="Success"
              style={{ width: "36px", height: "36px" }}
            />
          </SuccessIconContainer>
          <SuccessText>
            Successfully added {successfulWorkers.length} crew member
            {successfulWorkers.length !== 1 ? "s" : ""}.
          </SuccessText>
        </SuccessSection>
      )}

      {failedUsers.length > 0 && (
        <FailedSection>
          <FailedHeader>
            <FailedIconContainer>
              <img
                src={`${assetUrl}/icons/Timesheets/message-error.svg`}
                alt="Error"
                style={{ width: "20px", height: "20px" }}
              />
            </FailedIconContainer>
            <FailedContent>
              <FailedTitle>
                Failed to add {failedUsers.length} crew member{failedUsers.length !== 1 ? "s" : ""}:
              </FailedTitle>
              <FailedWorkersList>
                {failedUsers.map((worker) => (
                  <FailedWorkerItem key={worker.userId}>
                    <StyledUserAvatar
                      fullName={worker.userName}
                      imgSrc={worker.profilePicSrc}
                      size={"md"}
                    />
                    <WorkerInfo>
                      <WorkerName>{worker.userName}</WorkerName>
                      <ErrorMessage>{worker.errorMessage}</ErrorMessage>
                    </WorkerInfo>
                  </FailedWorkerItem>
                ))}
              </FailedWorkersList>
            </FailedContent>
          </FailedHeader>
        </FailedSection>
      )}
    </PartialSuccessContainer>
  );
};
